import { A11yModule } from "@angular/cdk/a11y";
import { Directionality } from "@angular/cdk/bidi";
import { NgFor, NgIf, NgStyle } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  NgZone,
  Optional,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { Router } from "@angular/router";
import { NzConfigService, WithConfig } from "ng-zorro-antd/core/config";
import { NzOutletModule } from "ng-zorro-antd/core/outlet";
import { NzSafeAny } from "ng-zorro-antd/core/types";
import { InputBoolean } from "ng-zorro-antd/core/util";
import { NzTabSetComponent } from "ng-zorro-antd/tabs";
import { UiSizeLDSType } from "../../../core/models/ui-size.models";
import { UiTabNavItemDirective } from "../../directives/tab-nav-item.directive";
import { UiAnimatedInterface, UiTabChangeEvent, UiTabPosition, UiTabScrollEvent, UiTabType, UiTabsCanDeactivateFn } from "../../models/tab.models";
import { NZ_TAB_SET, UiTabComponent } from "../../tab.component";
import { UiTabBodyComponent } from "../tab-body/tab-body.component";
import { UiTabCloseButtonComponent } from "../tab-close/tab-close-button.component";
import { UiTabColorIndicatorComponent } from "../tab-color-indicator/tab-color-indicator.component";
import { UiTabNavBarComponent } from "../tab-nav-bar/tab-nav-bar.component";

@Component({
  selector: "ui-tabset",
  exportAs: "uiTabset",
  providers: [
    {
      provide: NZ_TAB_SET,
      useExisting: UiTabSetComponent,
    },
  ],
  templateUrl: "./tabset.component.html",
  styleUrls: ["tabset.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,

  host: {
    class: "ui-tabs",
    "[class.ui-tabs-card]": `nzType === 'card' || nzType === 'editable-card'`,
    "[class.ui-tabs-editable]": `nzType === 'editable-card'`,
    "[class.ui-tabs-editable-card]": `nzType === 'editable-card'`,
    "[class.ui-tabs-centered]": `nzCentered`,
    "[class.ui-tabs-rtl]": `dir === 'rtl'`,
    "[class.ui-tabs-top]": `nzTabPosition === 'top'`,
    "[class.ui-tabs-bottom]": `nzTabPosition === 'bottom'`,
    "[class.ui-tabs-left]": `nzTabPosition === 'left'`,
    "[class.ui-tabs-right]": `nzTabPosition === 'right'`,
    "[class.ui-tabs-default]": `nzSize === 'default'`,
    "[class.ui-tabs-small]": `nzSize === 'small'`,
    "[class.ui-tabs-large]": `nzSize === 'large'`,
  },
  standalone: true,
  imports: [
    UiTabNavBarComponent,
    NgStyle,
    NgFor,
    UiTabNavItemDirective,
    NgIf,
    UiTabCloseButtonComponent,
    UiTabColorIndicatorComponent,
    UiTabBodyComponent,
    NzOutletModule,
    A11yModule,
  ],
})
export class UiTabSetComponent extends NzTabSetComponent {
  @Input("uiSelectedIndex")
  get uiSelectedIndex(): number | null {
    return this.nzSelectedIndex;
  }
  set uiSelectedIndex(value: number | null) {
    this.nzSelectedIndex = value;
  }
  @Input("uiTabPosition") public nzTabPosition: UiTabPosition = "top";
  @Input("uiTabBarExtraContent") public nzTabBarExtraContent?: TemplateRef<void>;
  @Input("uiCanDeactivate") public nzCanDeactivate: UiTabsCanDeactivateFn | null = null;
  @Input("uiAddIcon") public nzAddIcon: string | TemplateRef<NzSafeAny> = "plus";
  @Input("uiTabBarStyle") public nzTabBarStyle: { [key: string]: string } | null = null;
  @Input("uiType") @WithConfig() public nzType: UiTabType = "line";
  @Input("uiSize") @WithConfig() public nzSize: UiSizeLDSType = "default";
  @Input("uiAnimated") @WithConfig() public nzAnimated: UiAnimatedInterface | boolean = false;
  @Input("uiTabBarGutter") @WithConfig() public nzTabBarGutter?: number = undefined;
  @Input("uiHideAdd") @InputBoolean() public nzHideAdd = false;
  @Input("uiCentered") @InputBoolean() public nzCentered = false;
  @Input("uiHideAll") @InputBoolean() public nzHideAll = false;
  @Input() @InputBoolean() public uiHideBody = false;
  @Input("uiLinkRouter") @InputBoolean() public nzLinkRouter = false;
  @Input("uiLinkExact") @InputBoolean() public nzLinkExact = true;

  @Output("uiSelectChange") public readonly nzSelectChange: EventEmitter<UiTabChangeEvent> = new EventEmitter<UiTabChangeEvent>(true);
  @Output("uiSelectedIndexChange") public readonly nzSelectedIndexChange: EventEmitter<number> = new EventEmitter<number>();
  @Output("uiTabListScroll") public readonly nzTabListScroll = new EventEmitter<UiTabScrollEvent>();
  @Output("uiClose") public readonly nzClose = new EventEmitter<{ index: number }>();
  @Output("uiAdd") public readonly nzAdd = new EventEmitter<void>();

  // Pick up only direct descendants under ivy rendering engine
  // We filter out only the tabs that belong to this tab set in `tabs`.
  @ContentChildren(UiTabComponent, { descendants: true })
  public allTabs: QueryList<UiTabComponent> = new QueryList<UiTabComponent>();
  @ViewChild(UiTabNavBarComponent, { static: false }) public tabNavBarRef!: UiTabNavBarComponent;

  public tabs: QueryList<UiTabComponent>;

  constructor(public uiConfigService: NzConfigService, ngZone: NgZone, cdr: ChangeDetectorRef, @Optional() directionality: Directionality, @Optional() router: Router) {
    super(uiConfigService, ngZone, cdr, directionality, router);
  }

  public getTabContentId(i: number): string {
    return super.getTabContentId(i).replace("nz-", "ui-");
  }
}
